export function getUploadElements(root) {
    const input = root.querySelector('[data-upload-input]');
    const drop = root.querySelector('[data-upload-drop]');
    const list = root.querySelector('[data-upload-files]');
    const template = root.querySelector('[data-upload-file-template]');

    if (!input || !drop || !list || !template) return null;

    return { input, drop, list, template };
}

export function createUploadState(root, elements) {
    const field = root.closest('[data-form-field]');
    const fieldName = root.dataset.name || elements.input.name;

    return {
        root,
        ...elements,
        field,
        fieldName,
        validationMessage: getValidationMessage(field, fieldName),
        uploadUrl: root.dataset.uploadUrl,
        removeUrl: root.dataset.removeUrl,
        maxFiles: toNumber(root.dataset.maxFiles, 1),
        maxFileSize: mb(root.dataset.maxFileSizeMb),
        maxTotalSize: mb(root.dataset.maxTotalSizeMb),
        required: root.dataset.required === 'true',
        extensions: parseExtensions(root.dataset.acceptedExtensions),
        files: new Map(),
    };
}

export function getPrefilledFiles(state) {
    const json = state.root.dataset.prefill;

    if (!json?.trim()) return [];

    try {
        const parsed = JSON.parse(json);
        const items = Array.isArray(parsed) ? parsed : [];

        return items
            .map(normalizePrefilledFile)
            .filter((file) => file.id);
    } catch (error) {
        console.error('Invalid upload prefill JSON:', error);
        return [];
    }
}

export function normalizePrefilledFile(file) {
    if (typeof file === 'string') {
        return { id: file, name: file, size: 0 };
    }

    const id =
        file.id ??
        file.Id ??
        file.fileId ??
        file.FileId ??
        file.uploadId ??
        file.UploadId ??
        file.value ??
        file.Value;

    return {
        id,
        name:
            file.name ??
            file.Name ??
            file.fileName ??
            file.FileName ??
            file.label ??
            file.Label ??
            id,
        size: file.size ?? file.Size ?? file.fileSize ?? file.FileSize ?? 0,
    };
}

export function validateSelectedFile(state, file) {
    if (getUploadedCount(state) >= state.maxFiles) return state.root.dataset.errorMaxFiles;
    if (state.files.size >= state.maxFiles) return state.root.dataset.errorMaxFiles;
    if (file.size === 0) return state.root.dataset.errorEmpty;
    if (state.maxFileSize && file.size > state.maxFileSize) return state.root.dataset.errorFileSize;
    if (state.maxTotalSize && getTotalSize(state) + file.size > state.maxTotalSize) return state.root.dataset.errorTotalSize;

    if (state.extensions.length && !state.extensions.includes(getFileExtension(file.name))) {
        return state.root.dataset.errorExtension;
    }

    const fileNameError = validateFileName(state, file.name);
    if (fileNameError) return fileNameError;

    return '';
}

export function createUploadFormData(state, file) {
    const fileFieldName = state.root.dataset.fileFieldName || 'File';
    const formData = new FormData();

    formData.append(fileFieldName, file);

    const token = getRequestVerificationToken(state.root);

    if (token) {
        formData.append('__RequestVerificationToken', token);
    }

    return formData;
}

export function createRemoveFormData(state, serverId) {
    const formData = new FormData();

    formData.append('fileReference', serverId);
    formData.append('id', serverId);

    const token = getRequestVerificationToken(state.root);

    if (token) {
        formData.append('__RequestVerificationToken', token);
    }

    return formData;
}

export function getServerId(response) {
    return (
        response.id ??
        response.Id ??
        response.fileId ??
        response.FileId ??
        response.uploadId ??
        response.UploadId ??
        response.name ??
        response.Name
    );
}

export function getUploadedCount(state) {
    return [...state.files.values()].filter((entry) => Boolean(entry.serverId)).length;
}

export function getTotalSize(state) {
    return [...state.files.values()].reduce((total, entry) => total + entry.file.size, 0);
}

export function hasUploadedReferences(state) {
    return [...state.files.values()].some((entry) => Boolean(entry.serverId));
}

export function hasPendingUploads(state) {
    return [...state.files.values()].some((entry) => !entry.serverId && !entry.prefilled);
}

export function setHiddenFileValue(hidden, name, value) {
    if (!hidden) return;

    hidden.name = name;
    hidden.value = value;
}

export function dispatchUploadEvent(state, name, detail = undefined) {
    state.root.dispatchEvent(
        new CustomEvent(name, {
            bubbles: true,
            detail,
        })
    );
}

export function setValidationMessage(validationMessage, message) {
    if (!validationMessage) return;

    validationMessage.textContent = message;
    validationMessage.classList.remove('field-validation-valid');
    validationMessage.classList.add('field-validation-error');
    validationMessage.dataset.formFieldMessageSource = 'client';
}

export function clearValidationMessage(validationMessage) {
    if (validationMessage?.dataset.formFieldMessageSource !== 'client') return;

    validationMessage.textContent = '';
    validationMessage.classList.remove('field-validation-error');
    validationMessage.classList.add('field-validation-valid');
    delete validationMessage.dataset.formFieldMessageSource;
}

export function xhrPost(url, formData, onProgress) {
    return new Promise((resolve, reject) => {
        if (!url) {
            reject(new Error('Missing upload URL.'));
            return;
        }

        const request = new XMLHttpRequest();

        request.open('POST', url);

        request.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };

        request.onload = () => {
            const body = parseJsonResponse(request.responseText);

            if (request.status < 200 || request.status >= 300) {
                reject(new Error(body.error || body.Error || body.message || body.Message || request.statusText || 'Upload failed.'));
                return;
            }

            resolve(body);
        };

        request.onerror = () => reject(new Error('Network error.'));
        request.send(formData);
    });
}

export function formatBytes(value) {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        return '';
    }

    if (numericValue < 1024 * 1024) {
        return `${Math.round(numericValue / 1024)} KB`;
    }

    return `${(numericValue / 1024 / 1024).toFixed(1)} MB`;
}

function getValidationMessage(field, name) {
    if (!field) return null;

    const escapedName = cssEscape(name || field.dataset.formFieldName || '');

    if (escapedName) {
        const specific = field.querySelector(`:scope > .error [data-valmsg-for="${escapedName}"]`);

        if (specific) return specific;
    }

    return field.querySelector(':scope > .error [data-valmsg-for]');
}

function getRequestVerificationToken(root) {
    return (
        root.querySelector('input[name="__RequestVerificationToken"]')?.value ||
        document.querySelector('input[name="__RequestVerificationToken"]')?.value ||
        ''
    );
}

function parseJsonResponse(value) {
    try {
        return value ? JSON.parse(value) : {};
    } catch {
        return {};
    }
}

function parseExtensions(value = '') {
    return value
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
        .map((item) => item.startsWith('.') ? item : `.${item}`);
}

function getFileExtension(fileName = '') {
    return `.${fileName.split('.').pop()?.toLowerCase()}`;
}

const ALLOWED_FILENAME_CHARS = /^[a-zA-Z0-9\-_.()\s]+$/;

function validateFileName(state, fileName) {
    if (!fileName || ALLOWED_FILENAME_CHARS.test(fileName)) return '';

    return state.root.dataset.errorFilename || 'The file name contains invalid characters.';
}

function toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function mb(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed * 1024 * 1024 : 0;
}

function cssEscape(value) {
    if (window.CSS?.escape) {
        return CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
}